import type { Patient } from "@patients/patientsClient";
import { usePatientSignalment, usePatientSprite } from "@patients/signalment";
import { formatPhoneNumber } from "@phone/phoneSetupClient";
import { PatientHeader } from "@scribenote/brian-design-system";

import { useHousehold } from "../households/householdsClient";

const joined = (...parts: (string | null | undefined)[]): string | null =>
  parts.filter(Boolean).join(" · ") || null;

export const PatientBand = ({ patient }: { patient: Patient }) => {
  const { data: household } = useHousehold(patient.householdId);
  const { data: signalment } = usePatientSignalment(patient.id);
  const { data: sprite } = usePatientSprite(patient.id);

  const members = (household?.clients ?? [])
    .map((client) =>
      joined(
        client.name,
        client.phone ? formatPhoneNumber(client.phone) : null,
        client.email,
      ),
    )
    .filter(Boolean)
    .join("; ");
  const owner = household
    ? [household.label, members].filter(Boolean).join(": ")
    : null;
  const fields = [
    {
      label: "Species · breed",
      value: joined(signalment?.species, signalment?.breed),
    },
    { label: "Sex · age", value: joined(signalment?.sex, signalment?.age) },
    { label: "Microchip", value: signalment?.microchip ?? null },
  ].filter((field) => field.value);

  return (
    <section
      aria-label="Patient"
      className="shrink-0 rounded-md border border-rule bg-card px-7 py-4 shadow-sheet"
    >
      <PatientHeader
        size="workstation"
        name={patient.name}
        meta={owner}
        sprite={sprite ?? patient.sprite}
      />
      {fields.length > 0 && (
        <dl className="mt-2.5 flex flex-wrap gap-x-12 gap-y-2 border-t border-rule pt-3">
          {fields.map((field) => (
            <div key={field.label}>
              <dt className="font-mono text-[0.625rem] uppercase tracking-[0.14em] text-annotation">
                {field.label}
              </dt>
              <dd className="mt-[3px] whitespace-nowrap text-sm text-ink">
                {field.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
};
